import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { usePostHog } from "@posthog/react";
import { ApiError } from "../lib/api";
import { captureUserLoggedIn, captureUserLoggedOut } from "../lib/analytics";
import { authApi } from "../lib/auth";
import { AUTH_EVENTS } from "../lib/authEvents";
import { AuthContext } from "./authContextValue";
import type {
  AuthUserResponseDto,
  LoginRequestDto,
  SignupRequestDto,
} from "../types/types";

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const posthog = usePostHog();
  const [user, setUser] = useState<AuthUserResponseDto | null>(null);
  const [status, setStatus] = useState<"loading" | "authenticated" | "anonymous">("loading");

  const identifyUser = useCallback((authUser: AuthUserResponseDto) => {
    posthog?.identify(authUser.id, {
      email: authUser.email,
      firstName: authUser.firstName,
      lastName: authUser.lastName,
      roles: authUser.roles,
    });
  }, [posthog]);

  const clearAuthState = useCallback(() => {
    setUser(null);
    setStatus("anonymous");
  }, []);

  const initialize = useCallback(async () => {
    try {
      await authApi.csrf();
      const authUser = await authApi.me();
      setUser(authUser);
      setStatus("authenticated");
      identifyUser(authUser);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        clearAuthState();
        return;
      }

      clearAuthState();
      throw error;
    }
  }, [clearAuthState, identifyUser]);

  useEffect(() => {
    let cancelled = false;

    const initializeFromSession = async () => {
      try {
        await authApi.csrf();
        const authUser = await authApi.me();
        if (!cancelled) {
          setUser(authUser);
          setStatus("authenticated");
          identifyUser(authUser);
        }
      } catch {
        if (!cancelled) {
          clearAuthState();
        }
      }
    };

    void initializeFromSession();

    return () => {
      cancelled = true;
    };
  }, [clearAuthState, identifyUser]);

  useEffect(() => {
    const handleLogoutEvent = () => {
      clearAuthState();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_EVENTS.logoutStorageKey || !event.newValue) {
        return;
      }
      handleLogoutEvent();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(AUTH_EVENTS.logoutEventName, handleLogoutEvent);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(AUTH_EVENTS.logoutEventName, handleLogoutEvent);
    };
  }, [clearAuthState]);

  const login = useCallback(async (payload: LoginRequestDto) => {
    await authApi.csrf();
    const authUser = await authApi.login(payload);
    setUser(authUser);
    setStatus("authenticated");
    identifyUser(authUser);
    captureUserLoggedIn(posthog, { roles: authUser.roles });
    return authUser;
  }, [identifyUser, posthog]);

  const signup = useCallback(async (payload: SignupRequestDto) => {
    await authApi.csrf();
    const authUser = await authApi.signup(payload);
    clearAuthState();
    return authUser;
  }, [clearAuthState]);

  const logout = useCallback(async () => {
    await authApi.csrf();
    await authApi.logout();
    captureUserLoggedOut(posthog);
    posthog?.reset();
    clearAuthState();
  }, [clearAuthState, posthog]);

  const value = useMemo(
    () => ({
      user,
      status,
      isAuthenticated: status === "authenticated",
      initialize,
      login,
      signup,
      logout,
    }),
    [initialize, login, logout, signup, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
